const RIVHIT_DOCUMENTS_DIRTY_KEY = 'rivhit_documents_dirty_at';
const RIVHIT_DOCUMENTS_EVENT = 'rivhit-documents-updated';
const RIVHIT_DOCUMENT_LINKS_KEY = 'rivhit_document_links';

interface DocumentLinkRecord {
  document_type: number;
  document_number: number;
  document_link: string;
  updated_at: number;
}

function makeDocumentKey(documentType: number, documentNumber: number): string {
  return `${documentType}:${documentNumber}`;
}

function readDocumentLinks(): Record<string, DocumentLinkRecord> {
  try {
    const raw = localStorage.getItem(RIVHIT_DOCUMENT_LINKS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, DocumentLinkRecord>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeDocumentLinks(records: Record<string, DocumentLinkRecord>): void {
  localStorage.setItem(RIVHIT_DOCUMENT_LINKS_KEY, JSON.stringify(records));
}

export function markRivhitDocumentsDirty(): void {
  const timestamp = String(Date.now());
  localStorage.setItem(RIVHIT_DOCUMENTS_DIRTY_KEY, timestamp);
  window.dispatchEvent(new CustomEvent(RIVHIT_DOCUMENTS_EVENT, { detail: { timestamp } }));
}

export function rememberRivhitDocumentLink(documentType: number, documentNumber: number, documentLink?: string): void {
  if (!documentNumber || !documentLink) {
    console.log('[RivhitDocs] Skipping remember link', {
      documentType,
      documentNumber,
      hasLink: Boolean(documentLink),
      timestamp: new Date().toISOString(),
    });
    return;
  }
  const records = readDocumentLinks();
  const key = makeDocumentKey(documentType || 0, documentNumber);
  records[key] = {
    document_type: documentType,
    document_number: documentNumber,
    document_link: documentLink,
    updated_at: Date.now(),
  };
  writeDocumentLinks(records);
  console.log('[RivhitDocs] Remembered document link', {
    key,
    documentType,
    documentNumber,
    documentLink,
    storedKeys: Object.keys(records),
    timestamp: new Date().toISOString(),
  });
}

export function getRememberedRivhitDocumentLink(documentType: number, documentNumber: number): string | undefined {
  const records = readDocumentLinks();
  const exact = records[makeDocumentKey(documentType, documentNumber)]?.document_link;
  if (exact) {
    console.log(
      `[RivhitDocs] Found exact remembered link type=${documentType} number=${documentNumber} ts=${new Date().toISOString()}`
    );
    return exact;
  }

  const fallback = Object.values(records)
    .filter((record) => record.document_number === documentNumber)
    .sort((a, b) => b.updated_at - a.updated_at)[0];
  console.log(
    `[RivhitDocs] Lookup remembered link type=${documentType} number=${documentNumber} ` +
    `foundFallback=${Boolean(fallback?.document_link)} records=` +
    `${Object.values(records).map((record) => `${record.document_type}:${record.document_number}:${record.document_link ? '1' : '0'}`).join(',')} ` +
    `ts=${new Date().toISOString()}`
  );
  return fallback?.document_link;
}

export function getRivhitDocumentsDirtyAt(): number | null {
  const value = localStorage.getItem(RIVHIT_DOCUMENTS_DIRTY_KEY);
  if (!value) return null;
  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function clearRivhitDocumentsDirty(): void {
  localStorage.removeItem(RIVHIT_DOCUMENTS_DIRTY_KEY);
}

export function subscribeToRivhitDocumentsDirty(listener: () => void): () => void {
  const handleDirty = () => listener();
  const handleStorage = (event: StorageEvent) => {
    if (event.key === RIVHIT_DOCUMENTS_DIRTY_KEY) {
      listener();
    }
  };

  window.addEventListener(RIVHIT_DOCUMENTS_EVENT, handleDirty);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(RIVHIT_DOCUMENTS_EVENT, handleDirty);
    window.removeEventListener('storage', handleStorage);
  };
}
